/**
 * Wavlynq Store — buyer cart store (Zustand, persisted to localStorage).
 *
 * Multi-vendor carts are allowed. At checkout we use the
 * "Separate Charges & Transfers" pattern: a single buyer charge to
 * Wavlynq, then the webhook fans out per-vendor transfers (vendor net
 * of the 10% platform fee). The cart still tracks vendor_id per line
 * so the checkout edge function can build per-vendor sub-orders.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type CartLine = {
  product_id: string;
  variant_id: string | null;
  vendor_id: string;
  vendor_slug: string;
  vendor_name: string;
  product_slug: string;
  title: string;
  variant_title?: string | null;
  unit_price_cents: number;
  currency: string;
  quantity: number;
  image_url?: string | null;
  product_type: "physical" | "digital";
  max_stock?: number | null;
};

type CartState = {
  lines: CartLine[];
  add: (line: CartLine) => void;
  remove: (product_id: string, variant_id: string | null) => void;
  setQuantity: (
    product_id: string,
    variant_id: string | null,
    quantity: number,
  ) => void;
  clear: () => void;
  totalCents: () => number;
  vendorGroups: () => Record<string, CartLine[]>;
  itemCount: () => number;
  hasPhysical: () => boolean;
};

const sameLine = (a: CartLine, b: { product_id: string; variant_id: string | null }) =>
  a.product_id === b.product_id && (a.variant_id ?? null) === (b.variant_id ?? null);

export const useStoreCart = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      add: (line) =>
        set((s) => {
          const idx = s.lines.findIndex((l) => sameLine(l, line));
          if (idx === -1) return { lines: [...s.lines, line] };
          const next = [...s.lines];
          const newQty = next[idx].quantity + line.quantity;
          next[idx] = {
            ...next[idx],
            quantity:
              line.max_stock != null ? Math.min(newQty, line.max_stock) : newQty,
          };
          return { lines: next };
        }),
      remove: (product_id, variant_id) =>
        set((s) => ({
          lines: s.lines.filter((l) => !sameLine(l, { product_id, variant_id })),
        })),
      setQuantity: (product_id, variant_id, quantity) =>
        set((s) => ({
          lines: s.lines
            .map((l) =>
              sameLine(l, { product_id, variant_id })
                ? { ...l, quantity: Math.max(0, quantity) }
                : l,
            )
            .filter((l) => l.quantity > 0),
        })),
      clear: () => set({ lines: [] }),
      totalCents: () =>
        get().lines.reduce((sum, l) => sum + l.unit_price_cents * l.quantity, 0),
      vendorGroups: () => {
        const groups: Record<string, CartLine[]> = {};
        for (const l of get().lines) {
          (groups[l.vendor_id] ||= []).push(l);
        }
        return groups;
      },
      itemCount: () => get().lines.reduce((n, l) => n + l.quantity, 0),
      hasPhysical: () => get().lines.some((l) => l.product_type === "physical"),
    }),
    { name: "wavlynq-store-cart-v1" },
  ),
);

export const formatPrice = (cents: number, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
